import {
    AssetCreateUpdateRequest,
    AssetType as AssetTypeV2,
    AspectRatioRequest,
    AssetResponse
} from "@switcherstudio/api-core-client";
import {
    FileWithMetadata,
    AssetLayering,
    AspectRatioObject,
    Thumbnail,
    FileProps
} from "views/page-content/cloud/upload/types";
import { determineFileType } from "views/page-content/cloud/upload/upload-helpers/helpers";
import { v4 as uuidv4 } from "uuid";
import get from "lodash/get";
import { UppyFile } from "@uppy/core";
import rollbar from "./rollbar";
import { AnonymousCredential, BlockBlobClient } from "@azure/storage-blob";

export type FileItemV2 = {
    name: string;
    file: File;
    thumbnail?: Thumbnail;
    assetLayering?: AssetLayering;
    assetType?: AssetTypeV2;
    fileProps?: FileProps;
    aspectRatios?: AspectRatioObject[];
    videoRef?: any;
};

// V2 version that works with UppyFiles and fileAttributes
export async function getFilesWithMetadataV2(
    files: UppyFile[], // from modal
    fileAttributes: { [fileName: string]: any }
): Promise<FileWithMetadata[]> {
    return Promise.all(
        files.map((file) =>
            assignFileMetadataV2(file, fileAttributes[file.id] || {})
        )
    );
}

async function assignFileMetadataV2(
    file: any, // UppyFile
    attributes: any
): Promise<FileWithMetadata> {
    const fileMid = await getOrCreateMidV2(file);
    const metadataMid = uuidv4();
    const assetType = getAssetTypeV2(file.type);

    // Convert attributes to proper format
    const targetAspectRatio: AspectRatioObject =
        assetType !== "Audio"
            ? convertOrientationToAspectRatio(attributes.orientation)
            : { Width: 16, Height: 9 };

    // IMPORTANT:
    // - "uppyFile" is the Uppy wrapper (has id/name/type/data)
    // - "rawFile" is the native browser File (actual bytes)
    const uppyFile = file as UppyFile;
    const rawFile = (uppyFile?.data || file) as File;

    const uppyId =
        (uppyFile as any)?.id ??
        (file as any)?.id ??
        (() => {
            rollbar.warning(
                "assignFileMetadataV2: missing uppy file id; falling back to rawFile.name"
            );
            return rawFile?.name;
        })();

    // Create FileItemV2 structure from UppyFile and attributes
    const fileItem: FileItemV2 = {
        file: rawFile,
        name: (uppyFile as any)?.name ?? rawFile.name,
        assetLayering: attributes.layerOption || AssetLayering.FullScreen,
        assetType: assetType,
        aspectRatios: [targetAspectRatio],
        thumbnail: attributes.thumbnail || generateDefaultThumbnail(),
        fileProps: {
            tag: attributes.productionGroups?.[0] || "",
            audioOnly: attributes.audioOnly || false,
            enableAudio: attributes.enableAudio !== false,
            endOnBlack: attributes.endOnBlack || false,
            thumbnailTag: attributes.thumbnailTag || ""
        }
    };

    return {
        file: rawFile,
        uppyId,
        metadata: generateMetadataV2(
            fileItem.assetLayering,
            assetType,
            fileMid,
            metadataMid,
            fileItem.thumbnail,
            fileItem.fileProps,
            targetAspectRatio
        ),
        mid: fileMid,
        imageMid: metadataMid,
        fileProps: fileItem.fileProps,
        aspectRatios: fileItem.aspectRatios
    };
}


function generateMetadataV2(
    assetLayering: AssetLayering,
    assetType: AssetTypeV2,
    fileMid: string,
    metadataMid: string,
    thumbnail: any,
    props: any,
    targetAspectRatio: AspectRatioObject
): Blob {
    if (assetType === "Video" && !props.audioOnly) {
        // video
        return generateVideoOrAudioMetadataV2(
            fileMid,
            metadataMid,
            thumbnail,
            true,
            props,
            targetAspectRatio
        );
    }

    if (assetType === "Audio" || (assetType === "Video" && props.audioOnly)) {
        // audio
        return generateVideoOrAudioMetadataV2(
            fileMid,
            metadataMid,
            thumbnail,
            false,
            props,
            targetAspectRatio
        );
    }

    if (assetLayering === AssetLayering.Overlay) {
        return generateOverlayMetadataV2(
            fileMid,
            metadataMid,
            thumbnail,
            targetAspectRatio
        );
    }

    return generateImageMetadataV2(
        fileMid,
        metadataMid,
        thumbnail,
        targetAspectRatio
    );
}

function getImageFormatFromDataURL(dataURL: string): string {
    if (!dataURL) return "png";
    const match = dataURL.match(/^data:image\/(png|jpeg|jpg|gif|webp)/i);
    if (match) {
        const fmt = match[1].toLowerCase();
        return fmt === "jpg" ? "jpeg" : fmt;
    }
    return "png";
}

function normalizeThumbnailFormat(format: unknown): string | undefined {
    if (typeof format !== "string") return;
    const fmt = format.toLowerCase();
    if (fmt === "jpg") return "jpeg";
    if (fmt === "png" || fmt === "jpeg" || fmt === "gif" || fmt === "webp") {
        return fmt;
    }
    return;
}

function generateImageMetadataV2(
    fileMid: string,
    metadataMid: string,
    thumbnail: any,
    targetAspectRatio: AspectRatioObject
): Blob {
    const runtimeFormat = normalizeThumbnailFormat((thumbnail as any)?.format);
    const format = runtimeFormat || getImageFormatFromDataURL(thumbnail?.dataURL);

    const originalWidth =
        (thumbnail as any)?.originalWidth ?? thumbnail?.width ?? 160;
    const originalHeight =
        (thumbnail as any)?.originalHeight ?? thumbnail?.height ?? 90;

    const template = `<?xml version="1.0" encoding="UTF-8" ?>
<mmart>
  <mid value="${metadataMid}" />
  <dep url="mid:${fileMid}" />
  <target-aspect-ratio value="${targetAspectRatio.Width}:${targetAspectRatio.Height}" />
  <rank value="1n"/>
  <artwork name="image">
      <props>
          <key>image</key>
          <image src="mid:${fileMid}" />
      </props>
  </artwork>
  <thumbnail format="${format}" width="${originalWidth}" height="${originalHeight}" rendering="composed">${thumbnail?.dataURL?.split(",", 2)[1] || ""}</thumbnail>
</mmart>`;
    return new Blob([template], { type: "application/vnd.switcher-mmart" });
}

function generateOverlayMetadataV2(
    fileMid: string,
    metadataMid: string,
    thumbnail: any,
    targetAspectRatio: AspectRatioObject
): Blob {
    const y = thumbnail?.art?.y || 0.5;
    const height = thumbnail?.art?.height || 0.2;

    const runtimeFormat = normalizeThumbnailFormat((thumbnail as any)?.format);
    const format = runtimeFormat || getImageFormatFromDataURL(thumbnail?.dataURL);

    const originalWidth =
        (thumbnail as any)?.originalWidth ?? thumbnail?.width ?? 160;
    const originalHeight =
        (thumbnail as any)?.originalHeight ?? thumbnail?.height ?? 90;

    const template = `<?xml version="1.0" encoding="UTF-8" ?>
<mmart>
  <mid value="${metadataMid}" />
  <dep url="mid:${fileMid}" />
  <target-aspect-ratio value="${targetAspectRatio.Width}:${targetAspectRatio.Height}" />
  <rank value="1n"/>
  <artwork name="logo">
      <props>
          <key>y</key>
          <real>${y}</real>
          <key>height</key>
          <real>${height}</real>
          <key>image</key>
          <image src="mid:${fileMid}" />
      </props>
  </artwork>
  <thumbnail format="${format}" width="${originalWidth}" height="${originalHeight}" rendering="composed">${thumbnail?.dataURL?.split(",", 2)[1] || ""}</thumbnail>
</mmart>`;
    return new Blob([template], { type: "application/vnd.switcher-mmart" });
}

function generateVideoOrAudioMetadataV2(
    fileMid: string,
    metadataMid: string,
    thumbnail: any,
    configureVideo: boolean,
    props: any,
    targetAspectRatio: AspectRatioObject
): Blob {
    const assetTypeSpecificTags = configureVideo
        ? `<target-aspect-ratio value="${targetAspectRatio.Width}:${targetAspectRatio.Height}" />
       <mconf audio="yes" video="yes"${props.enableAudio ? " " : ' pref-mute="true" '}${props.endOnBlack ? "" : 'end-frame="last"'}/>`
        : `<tag value="${props.thumbnailTag || ""}"/>
     <mconf audio="yes" video="no"/>`;

    const runtimeFormat = normalizeThumbnailFormat((thumbnail as any)?.format);
    const format = runtimeFormat || getImageFormatFromDataURL(thumbnail?.dataURL);

    const originalWidth =
        (thumbnail as any)?.originalWidth ?? thumbnail?.width ?? 160;
    const originalHeight =
        (thumbnail as any)?.originalHeight ?? thumbnail?.height ?? 90;

    const template = `<?xml version="1.0" encoding="UTF-8" ?>
<mmsrc type="played-asset">
<mid value="${metadataMid}" />
<dep url="mid:${fileMid}"/>
${assetTypeSpecificTags}
<thumbnail format="${format}" width="${originalWidth}" height="${originalHeight}" rendering="uncomposed">${thumbnail?.dataURL?.split(",", 2)[1] || ""}</thumbnail>
<asset src="mid:${fileMid}"/>
<rank value="1025n"/>
<artwork name="video"></artwork>
</mmsrc>`;

    return new Blob([template], { type: "application/vnd.switcher-mmsrc" });
}

async function getOrCreateMidV2(file: any): Promise<string> {
    try {
        const { extractMetadata } = await import("@switcherstudio/swimeta-ts");
        const existingMetadata = await extractMetadata(
            file.data || file,
            file.type
        );

        if (existingMetadata?.type === "qt") {
            const mid = get(
                existingMetadata,
                ["data", "com.recolive.media-id"],
                null
            );
            return mid !== null ? mid : uuidv4();
        } else if (existingMetadata?.type === "caf") {
            const mid = get(
                existingMetadata,
                ["data", "RecoLive Media ID"],
                null
            );
            return mid !== null ? mid : uuidv4();
        } else if (existingMetadata?.type === "xmp") {
            const { default: X2JS } = await import("x2js");
            const x2js = new X2JS();
            const data = (existingMetadata?.data || "") as string;
            const json = x2js.xml2js(data) as any;

            if (json) {
                const mid =
                    json?.xmpmeta?.RDF?.Description?.["_swi:MediaID"] ||
                    json?.xmpmeta?.RDF?.Description?.MediaID?.__text;
                return mid || uuidv4();
            }
        }
    } catch (error) {
        rollbar.info(
            "Error extracting asset metadata, generating new MID:",
            error
        );
    }

    return uuidv4();
}

function convertOrientationToAspectRatio(orientation: string): AspectRatioObject {
    if (orientation === "vertical") {
        return { Width: 9, Height: 16 };
    }
    // Default to horizontal
    return { Width: 16, Height: 9 };
}

function generateDefaultThumbnail() {
    return {
        width: 160,
        height: 90,
        dataURL: "data:image/png;base64,", // TODO: replace empty placeholder
        art: { y: 0.5, height: 0.2 }
    };
}

export async function assembleV2PutOptions(
    files: UppyFile[],
    fileAttributes: { [fileName: string]: any }
): Promise<{
    assetV2PutOptions: AssetCreateUpdateRequest[];
    filesV2WithMetadata: FileWithMetadata[];
}> {
    const filesV2WithMetadata: FileWithMetadata[] = await getFilesWithMetadataV2(
        files,
        fileAttributes
    );

    const assetV2PutOptions: AssetCreateUpdateRequest[] = [];

    filesV2WithMetadata.forEach((fileWithMetadata) => {
        const file = fileWithMetadata.file;

        // fileWithMetadata.file is a native File -> no ".id"
        // Use the stored Uppy id for attributes lookup
        const attributes = fileAttributes[fileWithMetadata.uppyId] || {};

        const { type, mimeType } = determineFileType(file);
        const assetType = getAssetTypeV2(type);

        const metadataExtension =
            assetType === "Video" || assetType === "Audio"
                ? ".mmsrc"
                : ".mmart";
        const isAudioOnlyFile =
            assetType === "Audio" ||
            (assetType === "Video" && attributes.audioOnly === true);

        // Convert orientation to AspectRatioRequest format
        const aspectRatios: AspectRatioRequest[] = [];
        if (attributes.orientation === "vertical") {
            aspectRatios.push({ Width: 9, Height: 16 });
        } else {
            aspectRatios.push({ Width: 16, Height: 9 });
        }

        // 1. Raw file asset
        assetV2PutOptions.push({
            Id: fileWithMetadata.mid,
            Name: file.name,
            Type: assetType,
            MimeType: mimeType,
            Tags: attributes.productionGroups || [],
            AspectRatios: assetType !== "Audio" ? aspectRatios : undefined,
            Meta: {
                layerOption: attributes.layerOption || "FullScreen",
                orientation: attributes.orientation || "horizontal",
                thumbnailTag: attributes.thumbnailTag || "",
                enableAudio: attributes.enableAudio,
                audioOnly: attributes.audioOnly,
                endOnBlack: attributes.endOnBlack,
                fileSize: file.size as any
            }
        });

        // 2. .mmart/.mmsrc
        assetV2PutOptions.push({
            Id: fileWithMetadata.imageMid,
            Name:
                file.name.substring(0, file.name.lastIndexOf(".")) +
                metadataExtension,
            Type: "Art",
            MimeType: fileWithMetadata.metadata.type,
            Tags: attributes.productionGroups || [],
            AspectRatios: !isAudioOnlyFile ? aspectRatios : undefined,
            RequiresAssetIds: [fileWithMetadata.mid], // Link to raw file
            Meta: {
                ...(isAudioOnlyFile && {
                    // API needs a JSON boolean; the generated typings model Meta values as JsonNode
                    PlayedSourceHasVideo: false as unknown as any
                })
            }
        });
    });

    return { assetV2PutOptions, filesV2WithMetadata };
}


export function getAssetTypeV2(type: string): AssetTypeV2 {
    const normalizedType = type.toLowerCase().trim();

    if (normalizedType.startsWith("image")) {
        return "Image";
    }

    if (normalizedType.startsWith("video")) {
        return "Video";
    }

    if (normalizedType.startsWith("audio")) {
        return "Audio";
    }

    if (normalizedType.startsWith("application")) {
        return "Art";
    }

    rollbar.warning(
        `Unrecognized asset MIME type: ${type}, defaulting to "Unknown"`
    );
    return "Image";
}

export async function uploadV2AssetToStorage(
    candidate: FileWithMetadata,
    asset: AssetResponse
) {
    try {
        const _candidate: FileWithMetadata = {
            ...candidate,
            aspectRatios: null
        };

        const azureUrlWithSasToken = asset.Details.Url || "";
        const blobServiceClient = new BlockBlobClient(
            azureUrlWithSasToken,
            new AnonymousCredential()
        );

        const target =
            _candidate.mid === asset.Details.Id ? _candidate.file : _candidate.metadata;

        const uploadBlockBlobOptions = {
            blobHTTPHeaders: { blobContentType: asset.Details.MimeType }
        };

        const uploadResponse = await blobServiceClient.uploadData(
            target,
            uploadBlockBlobOptions
        );

        if (uploadResponse.errorCode) {
            rollbar.error(
                "Error uploading asset to storage:",
                uploadResponse.errorCode
            );
        }
    } catch (err: any) {
        rollbar.error("Error uploading asset to storage", err);

        if (err?.code === "REQUEST_SEND_ERROR") {
            throw new Error("Error uploading asset to storage");
        }
    }
}
